import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  useTheme,
  Avatar,
  Divider,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Header from "components/Header";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDebitTabungan,
  fetchListKaryawanWithTabungan,
} from "state/karyawanActions";
import FlexStart from "components/FlexStart";
import { setMessage, setStatus } from "state";
import FloatingAlert from "components/FloatingAlert";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SavingsIcon from "@mui/icons-material/Savings";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { MoneyOutlined } from "@mui/icons-material";

// Schema validasi pakai Yup
const validationSchema = Yup.object({
  jumlah_tabungan: Yup.number(),
  jumlah_debit: Yup.number().required("Jumlah Setor wajib diisi"),
});

const SetorTabungan = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datatabungan } = location.state || {};
  const { status, message } = useSelector((state) => state.global);

  useEffect(() => {
    if (datatabungan) {
      formik.setFieldValue("id_karyawan", datatabungan.id_karyawan);
      formik.setFieldValue("jumlah_tabungan", datatabungan.saldo_saat_ini);
    }
  }, [datatabungan]);

  const handleCloseSnackbar = () => {
    navigate(-1);
    dispatch(fetchListKaryawanWithTabungan());
    formik.resetForm();
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
  };

  const formik = useFormik({
    initialValues: {
      id_karyawan: null,
      jumlah_tabungan: 0,
      jumlah_debit: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        id_karyawan: values.id_karyawan,
        jumlah_debit: values.jumlah_debit,
      };
      dispatch(fetchDebitTabungan(reqData));
    },
  });

  const formatRupiah = (amount) => {
    if (amount === null || amount === undefined) return "Rp 0";

    const absoluteAmount = Math.abs(amount);

    const formatted = absoluteAmount.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return `Rp ${formatted}`;
  };

  return (
    <Box p="2rem" maxWidth="900px" mx="auto">
      <Header
        title={`Setor Tabungan Karyawan`}
        subtitle="Tambah saldo tabungan karyawan dengan mudah"
      />

      <form onSubmit={formik.handleSubmit}>
        {datatabungan?.id_karyawan && (
          <Card
            elevation={3}
            sx={{
              mt: 3,
              borderRadius: 4,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            }}
          >
            {/* Header Card */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                p: 3,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  width: 56,
                  height: 56,
                }}
              >
                <SavingsIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Form Setor Tabungan
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Isi data di bawah untuk menambah tabungan
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {/* Employee Info Section */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.02)",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  INFORMASI KARYAWAN
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="ID Karyawan"
                      name="id_karyawan"
                      disabled
                      type="text"
                      value={formik.values.id_karyawan || "-"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalanceWalletIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Saldo Tabungan Saat Ini"
                      name="jumlah_tabungan"
                      disabled
                      type="text"
                      value={formatRupiah(formik.values.jumlah_tabungan)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Input Section */}
              <Box
                sx={{
                  mb: 4,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  mb={2}
                  fontWeight="bold"
                >
                  DETAIL SETORAN
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Jumlah Setor"
                      name="jumlah_debit"
                      type="text"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      value={String(formik.values.jumlah_debit)}
                      onChange={(e) => {
                        const { value } = e.target;
                        const digitsOnly = value.replace(/\D/g, "");
                        const cleanedValue =
                          digitsOnly.replace(/^0+/, "") || "0";
                        formik.setFieldValue(
                          "jumlah_debit",
                          Number(cleanedValue),
                        );
                      }}
                      error={Boolean(formik.errors.jumlah_debit)}
                      helperText={
                        formik.errors.jumlah_debit ||
                        "Masukkan jumlah setor dalam angka"
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyOutlined color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover": {
                            "& > fieldset": {
                              borderColor: theme.palette.secondary.main,
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Estimasi Total Tabungan Setelah Setor"
                      name="jumlah_tabungan"
                      disabled
                      type="text"
                      value={formatRupiah(
                        formik.values.jumlah_tabungan +
                          formik.values.jumlah_debit,
                      )}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SavingsIcon color="action" />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Action Buttons */}
              <FlexStart gap={2} sx={{ flexWrap: "wrap" }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={
                    formik.errors.jumlah_debit ||
                    formik.values.jumlah_debit === 0
                  }
                  startIcon={<AddCircleIcon />}
                  sx={{
                    background: theme.palette.secondary.main,
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: `0 4px 14px ${theme.palette.secondary.main}40`,
                    "&:hover": {
                      background: theme.palette.secondary[300],
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 20px ${theme.palette.secondary.main}50`,
                    },
                    "&:disabled": {
                      bgcolor: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                    },
                    color: "#ffffff",
                    transition: "all 0.3s ease",
                  }}
                >
                  Simpan
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    formik.resetForm();
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                      bgcolor: `${theme.palette.secondary.main}10`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    navigate(-1);
                    formik.resetForm();
                  }}
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "12px 28px",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    "&:hover": {
                      borderColor: theme.palette.error.dark,
                      color: theme.palette.error.dark,
                      bgcolor: `${theme.palette.error.main}10`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Batal
                </Button>
              </FlexStart>
            </CardContent>
          </Card>
        )}
      </form>
      <FloatingAlert
        open={message.length > 0}
        onClose={handleCloseSnackbar}
        message={message}
        severity={status === "failed" ? "error" : status}
      />
    </Box>
  );
};

export default SetorTabungan;
